import React, { ChangeEvent, useContext, useEffect, useRef, useState } from 'react'
import { EditDepartmentPropsTypes } from './EditDepartmentTypes'
import InputGroup from '../inputComp/InputGroup'
import { AddressIcon, DepartmentIcon, EmailIcon, FaxIcon, MobileIcon, NameIcon } from '../iconSet/IconSet'
import useInputValidate from '../../hooks/useInputValidate'
import { SelectItemTypes } from '../../CommonTypes'
import Container from '../containerComp/Container'
import { InputGroupPropsTypes } from '../inputComp/InputGroupType'
import { APICall, DATA_VALIDATION_ERROR } from '../../services/APIService'
import { API_DEPARTMENT_DELETE_URL, API_GET_ALL_DEPARTMENT_URL, API_GET_DEPARTMENT_BY_VALUE_URL, API_UPDATE_DEPARTMENT_URL, APIDeleteDepartmentType, APIGetAllDepartmentDataResponseType, APIGetDepartmentByValueType, APIUpdateDepartmentType } from '../../services/APIDepartmentService'
import { useAlert } from '../../context/AlertContext'
import { getRefreshToken } from '../../utils/APIUtils'

const EditDepartment = (props: EditDepartmentPropsTypes) => {

    const {showAlert} = useAlert()

    const [userDepartment, setUserDepartment] = useState<SelectItemTypes[]>([
        {value: 'all_departments', name: 'All Departments'},
        {value: 'dmc', name: 'DMC'},
        {value: 'ird-ews', name: 'IRD-EWS'},
        {value: 'la', name: 'LA'},
        {value: 'nbro', name: 'NBRO'},
        {value: 'ew_department', name: 'EW Department'},
        {value: 'ewd_department', name: 'EWD Department'},
        {value: 'thinklab', name: 'Thinklab'}
    ])
    
    // Loading screen
    const [isLoading, setIsLoading] = useState<boolean>(true)

    const departmentRef = useRef<HTMLSelectElement | null>(null)
    const organizationRef = useRef<HTMLInputElement | null>(null)
    const addressRef = useRef<HTMLInputElement | null>(null)
    const emailRef = useRef<HTMLInputElement | null>(null)
    const departmentNameRef = useRef<HTMLInputElement | null>(null)
    const telephoneNumberRef = useRef<HTMLInputElement | null>(null)
    const faxNumberRef = useRef<HTMLInputElement | null>(null)

    // Handle department drop box value change
    const handleDepartmentDropBox = async (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setIsLoading(true)
        const selectedValue: string = event.target.value

        // Get selected data
        const requestBody: APIGetDepartmentByValueType = {
            value: selectedValue
        }

        const response = await APICall(API_GET_DEPARTMENT_BY_VALUE_URL, 'POST', requestBody)

        if(response.data){
            const responseData: APIGetAllDepartmentDataResponseType = response.data

            if(organizationRef.current && addressRef.current && emailRef.current && departmentNameRef.current && telephoneNumberRef.current && faxNumberRef.current){
                organizationRef.current.value = responseData.organizationName
                addressRef.current.value = responseData.address
                emailRef.current.value = responseData.email
                departmentNameRef.current.value = responseData.name
                telephoneNumberRef.current.value = responseData.telephone
                faxNumberRef.current.value = responseData.faxNumber
            }
        }
        setIsLoading(false)
    }

    const formItems: InputGroupPropsTypes[] = [
        {
            type: 'select',
            icon: <DepartmentIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Select department',
            label: 'Department',
            componentSelectElement: departmentRef,
            optionList: userDepartment,
            required: true,
            name: 'department',
            onChange: handleDepartmentDropBox
        },
        {
            type: 'text',
            icon: <NameIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter organization name',
            label: 'Organization Name',
            name: 'organizationName',
            componentInputElement: organizationRef,
        },
        {
            type: 'text',
            icon: <AddressIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter address',
            label: 'Address',
            name: 'address',
            componentInputElement: addressRef,
        },
        {
            type: 'text',
            icon: <EmailIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter email',
            label: 'Email Address',
            name: 'email',
            componentInputElement: emailRef,
        },
        /* {
            type: 'text',
            icon: <EmailIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'hidden',
            label: 'hidden',
            name: 'hidden',
            notVisible: true, // Adding this property for hidden fields
        }, */
        {
            type: 'text',
            icon: <NameIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter department name',
            label: 'Department Name',
            name: 'departmentName',
            componentInputElement: departmentNameRef,
        },
        {
            type: 'text',
            icon: <MobileIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter telephone number',
            label: 'Telephone Number',
            name: 'telephoneNumber',
            componentInputElement: telephoneNumberRef,
        },
        {
            type: 'text',
            icon: <FaxIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter fax number',
            label: 'Fax Number',
            name: 'fax',
            componentInputElement: faxNumberRef,
        }
    ]

    const {validateField} = useInputValidate()

    /* Submit button click */
    const handleSubmitClick = async () => {
        setIsLoading(true)
        const department = validateField(departmentRef)
        // Get new access token
        getRefreshToken()

        const accessToken: string | null = window.localStorage.getItem('accessToken')

        if(accessToken){
            if(departmentRef.current && organizationRef.current && addressRef.current && emailRef.current && departmentNameRef.current && telephoneNumberRef.current && faxNumberRef.current){
                const requestBody: APIUpdateDepartmentType = {
                    accessToken: accessToken,
                    id: departmentRef.current.value,
                    organizationName: organizationRef.current.value,
                    email: emailRef.current.value,
                    faxNumber: faxNumberRef.current.value,
                    departmentName: departmentNameRef.current.value,
                    address: addressRef.current.value,
                    telephoneNumber: telephoneNumberRef.current.value
                }

                const response = await APICall(API_UPDATE_DEPARTMENT_URL, 'POST', requestBody)

                if(response.errorCode === DATA_VALIDATION_ERROR){
                    if(response.data){
                        console.log('data validation error: ', response.data)
                        showAlert('data validation error', 'ERROR')
                    }
                }
                else if(response.httpStatus === 'OK'){
                    showAlert('updated', 'SUCCESS')
                    console.log('updated')
                }
            }
        }
        setIsLoading(false)
    }

    /* Delete button click */
    const handleDeleteClick = async () => {
        setIsLoading(true)
        // Get new access token
        getRefreshToken()
        try
        {
            const accessToken: string | null = window.localStorage.getItem('accessToken')

            if(accessToken){
                if(departmentRef.current){
                    const requestBody: APIDeleteDepartmentType = {
                        accessToken: accessToken,
                        value: departmentRef.current.value
                    }
    
                    const response = await APICall(API_DEPARTMENT_DELETE_URL, 'POST', requestBody)
    
                    if(response.httpStatus === 'OK'){
                        if(departmentRef.current && organizationRef.current && addressRef.current && emailRef.current && departmentNameRef.current && telephoneNumberRef.current && faxNumberRef.current)
                        {
                            departmentNameRef.current.value = ''
                            organizationRef.current.value = ''
                            addressRef.current.value = ''
                            emailRef.current.value = ''
                            departmentNameRef.current.value = ''
                            telephoneNumberRef.current.value = ''
                            faxNumberRef.current.value = ''

                            showAlert('Department deleted successfully', 'SUCCESS')
                        }
                    }
                }
            }
        }
        catch(error)
        {
            console.log('department server not responding')
        }
        
        setIsLoading(false)
    }
    
    useEffect(() => {
        setIsLoading(true)
        getAllDepartments()
        setIsLoading(false)
    }, [])

    // Get all departments
    const getAllDepartments = async () => {
        try
        {
            const response = await APICall(API_GET_ALL_DEPARTMENT_URL, 'GET', null)

            if(response.data){
                const responseData: APIGetAllDepartmentDataResponseType[] = response.data
                const fetchedDepartments: SelectItemTypes[] = []
                
                responseData.map(item => {
                    fetchedDepartments.push({value: item.id, name: item.name})
                })
    
                setUserDepartment(fetchedDepartments)
            }
        }
        catch(error)
        {
            console.log('department server not responding')
        }
    }
    
    return (
        <Container title='edit department' isLoading={isLoading}>
            {/* Customization -> edit -> edit user */}
            <div className={`edit-department`}>
                <div className={`grid-layout-edit-department`}>   
                    <div className={`left-side`}>
                        {formItems.slice(0, 4).map((field, index) => (
                            <InputGroup key={index} {...field}/>
                        ))}
                    </div>

                    <div className={`right-side`}>
                    {formItems.slice(4).map((field, index) => (
                            <InputGroup key={index} {...field}/>
                        ))}
                    </div>

                    <div className={`button-wrapper`} style={{gridArea: 'button'}}>
                        <div className={`left-side`}></div>
                        <div className={`right-side`}>
                            <button className={`button-normal`} style={{backgroundColor: 'var(--color-main-red)'}} onClick={(e) => handleDeleteClick()}>delete</button>
                            <button className={`button-normal`} onClick={(e) => handleSubmitClick()}>submit</button>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    )
}

export default EditDepartment